<template>
  <v-container>
    <v-card>
      <v-toolbar color="primary" dark flat>
        <v-toolbar-title>Create Promotion</v-toolbar-title>
        <v-spacer />
        <v-btn text @click="save">
          Save
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.code" label="Code" outlined dense :error-messages="validationErrors.code" />
          </v-col>

          <v-col cols="12" md="6">
            <v-select label="Type" outlined dense :value="form.promotion_type_id"
                      :items="promotionTypeOptions" :error-messages="validationErrors.promotion_type_id"
                      @input="val => formInputChange(val, 'promotion_type_id')" />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field v-model="form.start_date" label="Start Date" type="date" outlined dense
                          :error-messages="validationErrors.start_date" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.end_date" label="End Date" type="date" outlined dense
                          :error-messages="validationErrors.end_date" />
          </v-col>

          <v-col cols="12">
            <v-switch v-model="form.is_active" label="Active" inset />
          </v-col>

          <!-- Dynamic Inputs Based on Selected Type -->
          <v-col v-if="selectedPromotionType.code === 'fix_disc'" cols="12">
            <fix-disc :amount="form.rule_config.amount" @update:amount="form.rule_config.amount = $event" />
          </v-col>

          <v-col v-else-if="selectedPromotionType.code === 'pct_disc'" cols="12">
            <pct-disc :percent="form.rule_config.percent" @update:percent="form.rule_config.percent = $event" />
          </v-col>

          <v-col v-else-if="selectedPromotionType.code === 'b1gx'" cols="12">
            <b1gx :buy-qty="form.rule_config.buy_qty" :get-qty="form.rule_config.get_qty"
                  @update:buyQty="form.rule_config.buy_qty = $event" @update:getQty="form.rule_config.get_qty = $event" />
          </v-col>

          <v-col v-else-if="selectedPromotionType.code === 'cross_freebie'" cols="12">
            <cross-freebie :buy-qty="form.rule_config.buy_qty" :free-stock-id="form.rule_config.free_stock_id"
                           :get-qty="form.rule_config.free_qty" :stock-options="stockOptions"
                           @update:buyQty="form.rule_config.buy_qty = $event"
                           @update:freeStockId="form.rule_config.free_stock_id = $event"
                           @update:getQty="form.rule_config.free_qty = $event" />
          </v-col>

          <v-col v-else-if="selectedPromotionType.code === 'x4y'" cols="12">
            <x4y :x="form.rule_config.x" :y="form.rule_config.y" @update:x="form.rule_config.x = $event"
                 @update:y="form.rule_config.y = $event" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from '@/extra-functions/axios.extra'
import LocalStore from '@/lib/localStore'
import { camelToSnake } from '@/lib/utils'

import FixDisc from './types/FixDisc.vue'
import PctDisc from './types/PctDisc.vue'
import B1gx from './types/B1gx.vue'
import CrossFreebie from './types/CrossFreebie.vue'
import X4y from './types/X4y.vue'
import { ErrorPopup, getCurrentDate } from '../../../../lib/utils'

const defaultForm = () => ({
  code: '',
  promotion_type_id: '',
  start_date: getCurrentDate(new Date(), 'sv-SE'),
  end_date: '',
  rule_config: {},
  is_active: true,
})

const localStore = new LocalStore({
  schema: { createPromotion: { ...defaultForm(), unsavedChanges: false } },
})

export default {
  name: 'CreatePromotion',
  components: {
    FixDisc,
    PctDisc,
    B1gx,
    CrossFreebie,
    X4y,
  },
  data() {
    return {
      form: defaultForm(),
      validationErrors: {},
      promotionTypeOptions: [],
      promotionTypeMap: new Map(),
      stockOptions: [],
    }
  },
  computed: {
    selectedPromotionType() {
      return this.promotionTypeMap.get(this.form.promotion_type_id) || {}
    },
  },
  watch: {
    form: {
      handler(val) {
        this.storeToLocal()
      },
      deep: true,
    },
  },
  created() {
    this.getLocalData()
    this.getPromotionTypes()
  },
  methods: {
    async getPromotionTypes() {
      try {
        const response = await axios(this.$store.state.auth.token).get('/promotion-types')
        this.promotionTypeOptions = response.data.map(type => ({
          text: type.name,
          value: type.id,
        }))
        const result = response.data.reduce((acc, type) => {
          acc.set(type.id, type)
          return acc
        }, new Map())
        this.promotionTypeMap = result
      } catch (e) {
        console.error('Failed to fetch promotion types', e)
      }
    },
    async save() {
      try {
        const payload = this.toSnakeCase(this.form)
        await axios(this.$store.state.auth.token).post('/promotions', payload)
        this.reset()
        this.$router.push('/promotions')
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error creating promotion:', e)
        }
        if (e?.response?.data?.errors) {
          const { errors } = e.response.data
          this.validationErrors = { ...errors }
          if ('rule_config' in errors) {
            ErrorPopup.fire('Validation Error', errors.rule_config[0])
          }
          return
        }
        ErrorPopup.fire('Error', 'Failed to create promotion')
      }
    },
    storeToLocal() {
      localStore.set({
        createPromotion: { ...this.form, unsavedChanges: true },
      })
    },
    getLocalData() {
      const saved = localStore.get('createPromotion')
      if (saved) this.form = { ...saved }
    },
    reset() {
      this.form = defaultForm()
      this.validationErrors = {}
      localStore.delete('createPromotion')
    },
    toSnakeCase(obj) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[camelToSnake(key)] = obj[key]
        return acc
      }, {})
    },
    /**
     * Description
     * @param {number} e
     * @param {string} attribute
     */
    formInputChange(val, attribute) {
      const newForm = { ...this.form, [attribute]: val }
      if (attribute === 'promotion_type_id') {
        const ruleConfig = { ...this.promotionTypeMap.get?.(val)?.default_config }
        newForm.rule_config = ruleConfig
      }
      this.form = newForm
    },
  },
}
</script>